import React from "react";
import { Box } from "@mui/material";
import { CasLoader } from "../brand/CasLoader";
import { LoadBoundary } from "../brand/LoadBoundary";
import { loadKey, loadStatus } from "../../hook/loadStatus";
import { useLoadError } from "../../slices/ui";
import { useAppIdRouteParamListener } from "../../hook/app";
import {
  useFetchNotebookPageIsPublishedListener,
  useFetchNotebookPageListener,
  useNotebookPageAccess,
  useNotebookPageRouteParamListener,
  useSelectedNotebookPage,
  useSelectedNotebookPageIdMap,
  useFetchNotebookPage,
  useSetNotePageTitleListener,
} from "../../hook/page";
import { useFetchFolderPropertyTypesListener } from "../../hook/page/property";
import { isDatabasePageData, isNotePageData } from "../../types/note";
import { Forbidden } from "../forbidden";
import { NotebookDatabasePage } from "../page/database";
import { NotebookNotePage } from "../page/page";

export const NotebookPage: React.FC = () => {
  const selectedNotebookPage = useSelectedNotebookPage();
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const pageError = useLoadError(selectedNotebookPageId ? loadKey("page", selectedNotebookPageId) : undefined);
  const retryPage = useFetchNotebookPage(selectedNotebookPageId);
  useAppIdRouteParamListener();
  useFetchFolderPropertyTypesListener(selectedNotebookPage?.["page-id"]);
  useFetchNotebookPageListener();
  useNotebookPageRouteParamListener();
  useFetchNotebookPageIsPublishedListener();
  useSetNotePageTitleListener();

  const pageData = selectedNotebookPage?.["page-data"];
  const { hasAccess, loading } = useNotebookPageAccess(
    selectedNotebookPage?.["page-id"],
  );

  if (!hasAccess && loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", paddingTop: 8 }}>
        <CasLoader />
      </Box>
    );
  }

  if (!hasAccess) {
    return <Forbidden />;
  }

  if (!pageData) {
    return (
      <LoadBoundary
        status={loadStatus(pageData, pageError)}
        onRetry={retryPage}
        label="Loading page"
        errorMessage="Could not load this page."
        testId="page-loading"
        fill
      />
    );
  }

  if (isNotePageData(pageData)) {
    return <NotebookNotePage />;
  }

  if (isDatabasePageData(pageData)) {
    return <NotebookDatabasePage />;
  }

  return <></>;
};
